const MAX_VIOLATIONS = 3;

export interface LockdownViolation {
  timestamp: string;
  type: 'TAB_SWITCH' | 'FULLSCREEN_EXIT' | 'WINDOW_BLUR' | 'DEVTOOLS' | 'COPY_PASTE' | 'RIGHT_CLICK' | 'SHORTCUT_BLOCKED';
  details?: string;
}

export interface LockdownState {
  isActive: boolean;
  isFullscreen: boolean;
  violationCount: number;
  violations: LockdownViolation[];
  shouldAutoSubmit: boolean;
  warningMessage: string | null;
}

type LockdownListener = (state: LockdownState) => void;

class ExamLockdownService {
  private active = false;
  private violationCount = 0;
  private violations: LockdownViolation[] = [];
  private listeners: Set<LockdownListener> = new Set();
  private warningMessage: string | null = null;
  private warningTimeout: ReturnType<typeof setTimeout> | null = null;
  private shouldAutoSubmit = false;

  private boundHandlers: {
    visibilityChange?: () => void;
    windowBlur?: () => void;
    windowFocus?: () => void;
    fullscreenChange?: () => void;
    keydown?: (e: KeyboardEvent) => void;
    contextmenu?: (e: Event) => void;
    copy?: (e: Event) => void;
    paste?: (e: Event) => void;
    cut?: (e: Event) => void;
    beforeunload?: (e: BeforeUnloadEvent) => void;
  } = {};

  getState(): LockdownState {
    return {
      isActive: this.active,
      isFullscreen: !!document.fullscreenElement,
      violationCount: this.violationCount,
      violations: [...this.violations],
      shouldAutoSubmit: this.shouldAutoSubmit,
      warningMessage: this.warningMessage,
    };
  }

  subscribe(listener: LockdownListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const state = this.getState();
    this.listeners.forEach((fn) => fn(state));
  }

  private addViolation(type: LockdownViolation['type'], details?: string): void {
    if (!this.active) return;

    this.violationCount++;
    this.violations.push({
      timestamp: new Date().toISOString(),
      type,
      details,
    });

    const remaining = MAX_VIOLATIONS - this.violationCount;
    if (remaining <= 0) {
      this.warningMessage = 'Maximum violations reached. Your test is being auto-submitted.';
      this.shouldAutoSubmit = true;
    } else {
      this.warningMessage = `Warning: Exam violation detected (${type.replace(/_/g, ' ').toLowerCase()}). ${remaining} violation${remaining === 1 ? '' : 's'} remaining before auto-submit.`;
      if (this.warningTimeout) clearTimeout(this.warningTimeout);
      this.warningTimeout = setTimeout(() => {
        this.warningMessage = null;
        this.notify();
      }, 6000);
    }

    this.notify();
  }

  async activate(): Promise<void> {
    if (this.active) return;
    this.active = true;
    this.violationCount = 0;
    this.violations = [];
    this.shouldAutoSubmit = false;
    this.warningMessage = null;

    this.attachEventListeners();
    await this.requestFullscreen();
    this.notify();
  }

  deactivate(): void {
    if (!this.active) return;
    this.active = false;

    this.detachEventListeners();
    this.exitFullscreen();

    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
    this.warningMessage = null;
    this.notify();
  }

  getViolations(): LockdownViolation[] {
    return [...this.violations];
  }

  private async requestFullscreen(): Promise<void> {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      }
    } catch {
      // Fullscreen not supported or denied by user
    }
  }

  private exitFullscreen(): void {
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    } catch {
      // Ignore
    }
  }

  private attachEventListeners(): void {
    this.boundHandlers.visibilityChange = () => {
      if (document.hidden && this.active) {
        this.addViolation('TAB_SWITCH', 'Student switched away from the exam tab');
      }
    };

    this.boundHandlers.windowBlur = () => {
      if (this.active) {
        this.addViolation('WINDOW_BLUR', 'Exam window lost focus');
      }
    };

    this.boundHandlers.fullscreenChange = () => {
      if (!document.fullscreenElement && this.active) {
        this.addViolation('FULLSCREEN_EXIT', 'Student exited fullscreen mode');
        // Re-request fullscreen after a short delay
        setTimeout(() => {
          if (this.active && !this.shouldAutoSubmit) {
            this.requestFullscreen();
          }
        }, 500);
      }
    };

    this.boundHandlers.keydown = (e: KeyboardEvent) => {
      if (!this.active) return;
      const blocked = this.isBlockedShortcut(e);
      if (blocked) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    this.boundHandlers.contextmenu = (e: Event) => {
      if (!this.active) return;
      e.preventDefault();
    };

    this.boundHandlers.copy = (e: Event) => {
      if (!this.active) return;
      e.preventDefault();
    };

    this.boundHandlers.paste = (e: Event) => {
      if (!this.active) return;
      e.preventDefault();
    };

    this.boundHandlers.cut = (e: Event) => {
      if (!this.active) return;
      e.preventDefault();
    };

    this.boundHandlers.beforeunload = (e: BeforeUnloadEvent) => {
      if (!this.active) return;
      e.preventDefault();
      e.returnValue = '';
    };

    document.addEventListener('visibilitychange', this.boundHandlers.visibilityChange);
    document.addEventListener('fullscreenchange', this.boundHandlers.fullscreenChange);
    window.addEventListener('blur', this.boundHandlers.windowBlur);
    window.addEventListener('beforeunload', this.boundHandlers.beforeunload);
    document.addEventListener('keydown', this.boundHandlers.keydown, true);
    document.addEventListener('contextmenu', this.boundHandlers.contextmenu, true);
    document.addEventListener('copy', this.boundHandlers.copy, true);
    document.addEventListener('paste', this.boundHandlers.paste, true);
    document.addEventListener('cut', this.boundHandlers.cut, true);
  }

  private detachEventListeners(): void {
    if (this.boundHandlers.visibilityChange) {
      document.removeEventListener('visibilitychange', this.boundHandlers.visibilityChange);
    }
    if (this.boundHandlers.fullscreenChange) {
      document.removeEventListener('fullscreenchange', this.boundHandlers.fullscreenChange);
    }
    if (this.boundHandlers.windowBlur) {
      window.removeEventListener('blur', this.boundHandlers.windowBlur);
    }
    if (this.boundHandlers.beforeunload) {
      window.removeEventListener('beforeunload', this.boundHandlers.beforeunload);
    }
    if (this.boundHandlers.keydown) {
      document.removeEventListener('keydown', this.boundHandlers.keydown, true);
    }
    if (this.boundHandlers.contextmenu) {
      document.removeEventListener('contextmenu', this.boundHandlers.contextmenu, true);
    }
    if (this.boundHandlers.copy) {
      document.removeEventListener('copy', this.boundHandlers.copy, true);
    }
    if (this.boundHandlers.paste) {
      document.removeEventListener('paste', this.boundHandlers.paste, true);
    }
    if (this.boundHandlers.cut) {
      document.removeEventListener('cut', this.boundHandlers.cut, true);
    }

    this.boundHandlers = {};
  }

  private isBlockedShortcut(e: KeyboardEvent): boolean {
    const ctrl = e.ctrlKey || e.metaKey;

    // Copy/paste/cut
    if (ctrl && ['c', 'v', 'x'].includes(e.key.toLowerCase())) return true;

    // Tab management: Ctrl+Tab, Ctrl+Shift+Tab, Ctrl+W, Ctrl+N, Ctrl+T
    if (ctrl && ['Tab', 'w', 'n', 't'].includes(e.key)) return true;

    // Dev tools: F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, Ctrl+U
    if (e.key === 'F12') return true;
    if (ctrl && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase())) return true;
    if (ctrl && e.key.toLowerCase() === 'u') return true;

    // Refresh: F5, Ctrl+R, Ctrl+Shift+R
    if (e.key === 'F5') return true;
    if (ctrl && e.key.toLowerCase() === 'r') return true;

    // Print: Ctrl+P
    if (ctrl && e.key.toLowerCase() === 'p') return true;

    // Print Screen
    if (e.key === 'PrintScreen') return true;

    // Alt+Tab detection (browser can only see keydown of Alt)
    if (e.altKey && e.key === 'Tab') return true;

    // Prevent Escape from exiting fullscreen (we handle it ourselves)
    if (e.key === 'Escape') return true;

    return false;
  }
}

const examLockdownService = new ExamLockdownService();
export default examLockdownService;
